import React, { useState, useEffect, useCallback } from 'react';
import { useContributionTypes } from '../hooks/useContributionTypes';
import {
  Box,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Select,
  HStack,
  VStack,
  Card,
  CardBody,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Text,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  SimpleGrid,
  Icon,
  useBreakpointValue,
  Stack,
  Divider,
} from '@chakra-ui/react';
import { FaCreditCard, FaPaypal } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import SumUpPaymentButton from '../components/Payment/SumUpPaymentButton';

const MODE_LABELS = {
  sumup: '💳 SumUp',
  carte_bancaire: '💳 Carte bancaire',
  stripe: '💳 Carte bancaire',
  paypal: '💰 PayPal',
  especes: '💵 Espèces',
  cheque: '📝 Chèque',
  virement: '🏦 Virement',
  autre: 'Autre',
};

// window.open renvoie toujours null quand « noopener » est demandé : l'application croyait alors la fenêtre
// bloquée et ne détectait jamais sa fermeture. On ouvre sans cette option, puis on coupe le lien avec la page.
const ouvrirFenetre = (url, nom, options) => {
  const fenetre = window.open(url, nom, options);
  if (fenetre) fenetre.opener = null;
  return fenetre;
};

export default function MemberContributions() {
  const { user } = useAuth();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  // Modals
  const { isOpen: isPaymentOpen, onOpen: onPaymentOpen, onClose: onPaymentClose } = useDisclosure();
  const { isOpen: isAnnualReceiptOpen, onOpen: onAnnualReceiptOpen, onClose: onAnnualReceiptClose } = useDisclosure();
  
  const { types: contributionTypes, loading: typesLoading } = useContributionTypes();
  const [contributions, setContributions] = useState([]);
  const [filteredContributions, setFilteredContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [amount, setAmount] = useState('');
  const [typeId, setTypeId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [receiptYear, setReceiptYear] = useState(new Date().getFullYear() - 1);

  const fetchContributions = useCallback(async () => {
    if (!user?.id) {
      console.log('User not loaded yet');
      return;
    }

    setLoading(true);
    try {
      console.log('Fetching contributions for user:', user.id);
      
      const response = await apiService.makeRequest(`/contributions?user_id=${user.id}`);
      
      console.log('Contributions API response:', response);
      
      // Gérer différents formats de réponse
      let contributionsData = [];
      if (Array.isArray(response)) {
        contributionsData = response;
      } else if (response.contributions && Array.isArray(response.contributions)) {
        contributionsData = response.contributions;
      } else if (response.data && Array.isArray(response.data)) {
        contributionsData = response.data;
      }
      
      console.log('Parsed contributions data:', contributionsData);
      console.log('Number of contributions:', contributionsData.length);
      
      setContributions(contributionsData);
      setFilteredContributions(contributionsData);
      
      if (contributionsData.length === 0) {
        console.log('No contributions found for this user');
      }
    } catch (error) {
      console.error('Error fetching contributions:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de charger les contributions',
        status: 'error',
        duration: 5000,
      });
      setContributions([]);
      setFilteredContributions([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, toast]);

  const filterContributions = useCallback(() => {
    let filtered = [...contributions];

    if (filterType !== 'all') {
      filtered = filtered.filter(c => c.type_id?.toString() === filterType);
    }

    if (filterYear !== 'all') {
      filtered = filtered.filter(c => 
        new Date(c.date_contribution).getFullYear().toString() === filterYear
      );
    }

    if (filterMonth !== 'all') {
      filtered = filtered.filter(c => 
        new Date(c.date_contribution).getMonth().toString() === filterMonth
      );
    }

    console.log('Filtered contributions:', filtered);
    setFilteredContributions(filtered);
  }, [contributions, filterType, filterMonth, filterYear]);

  useEffect(() => {
    fetchContributions();
  }, [fetchContributions]);

  useEffect(() => {
    filterContributions();
  }, [filterContributions]);

  // Gestion du retour du paiement
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    
    const provider = urlParams.get('provider');
    const paypalToken = urlParams.get('token');

    const showResult = (isPaid) => {
      if (isPaid) {
        toast({
          title: '✅ Paiement réussi',
          description: 'Votre cotisation a été enregistrée avec succès',
          status: 'success',
          duration: 5000,
        });
      } else {
        toast({
          title: '❌ Paiement échoué',
          description: 'Votre paiement n\'a pas abouti — aucun montant n\'a été débité de votre compte. Vous pouvez réessayer.',
          status: 'error',
          duration: 8000,
          isClosable: true,
        });
      }
      fetchContributions();
    };

    const showError = () => {
      toast({
        title: 'Erreur de vérification',
        description: 'Impossible de vérifier votre paiement. Contactez l\'administration.',
        status: 'error',
        duration: 6000,
      });
    };

    if (payment === 'success' && sessionId) {
      window.history.replaceState({}, document.title, '/member/contributions');
      apiService.makeRequest(`/contributions/payment/stripe/verify/${sessionId}`)
        .then((data) => showResult(data.is_paid))
        .catch(showError);
    } else if (payment === 'success' && provider === 'paypal' && paypalToken) {
      window.history.replaceState({}, document.title, '/member/contributions');
      apiService.makeRequest('/contributions/payment/paypal/capture', {
        method: 'POST',
        body: JSON.stringify({ orderId: paypalToken }),
      })
        .then((data) => showResult(data.is_paid))
        .catch(showError);
    } else if (payment === 'cancelled') {
      toast({
        title: 'Paiement annulé',
        description: 'Vous avez annulé le paiement',
        status: 'info',
        duration: 3000,
      });
      
      window.history.replaceState({}, document.title, '/member/contributions');
    }
  }, [fetchContributions, toast]);

  const handlePayment = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({
        title: 'Erreur',
        description: 'Veuillez entrer un montant valide',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!typeId) {
      toast({
        title: 'Erreur',
        description: 'Veuillez sélectionner un type de cotisation',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      // Récupérer le nom du type de cotisation
      const selectedType = contributionTypes.find(t => t.id === parseInt(typeId));
      
      let response;
      
      if (paymentMethod === 'sumup') {
        // Pour SumUp : processus en 2 étapes
        // 1. Créer d'abord la contribution
        const contributionResponse = await apiService.makeRequest('/contributions', {
          method: 'POST',
          body: JSON.stringify({
            montant: parseFloat(amount),
            type_id: parseInt(typeId),
            statut: 'en_attente',
            mode_paiement: 'sumup',
          }),
        });
        
        const contributionId = contributionResponse.contribution?.id || contributionResponse.id;
        
        if (!contributionId) {
          throw new Error('Erreur lors de la création de la cotisation');
        }
        
        // 2. Créer le checkout SumUp avec le contribution_id
        const returnUrl = `${window.location.origin}/member/contributions`;
        
        console.log('🔵 Creating SumUp checkout with:');
        console.log('   contribution_id:', contributionId, 'type:', typeof contributionId);
        console.log('   return_url:', returnUrl);
        
        response = await apiService.makeRequest('/payments/sumup/create-checkout', {
          method: 'POST',
          body: JSON.stringify({
            contribution_id: parseInt(contributionId),
            return_url: returnUrl,
          }),
        });
        
        console.log('✅ SumUp checkout response:', response);
      } else {
        // Pour Stripe/PayPal : utiliser l'ancien endpoint unifié
        response = await apiService.makeRequest('/contributions/payment/create', {
          method: 'POST',
          body: JSON.stringify({
            amount: parseFloat(amount),
            type_id: parseInt(typeId),
            contribution_type: selectedType?.nom || 'Cotisation',
            type_name: selectedType?.nom || 'Cotisation',
            paymentMethod: paymentMethod,
          }),
        });
      }

      if (paymentMethod === 'sumup') {
        // Ouvrir popup SumUp
        const width = 800;
        const height = 900;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        const popupFeatures = `
          width=${width},
          height=${height},
          left=${left},
          top=${top},
          toolbar=no,
          location=no,
          directories=no,
          status=no,
          menubar=no,
          scrollbars=yes,
          resizable=yes`.replace(/\s+/g, '');
        
        // Page interne avec le widget SumUp (SumUp n'a pas de page hébergée
        // accessible par simple redirection — le paiement carte doit se
        // faire via leur SDK JS, intégré dans notre propre page).
        const sumupUrl = `${window.location.origin}/sumup-checkout?checkout_id=${response.checkout_id}`;
        const sumupWindow = ouvrirFenetre(sumupUrl, 'SumUpPayment', popupFeatures);
        
        if (sumupWindow) {
          sumupWindow.focus();
          
          toast({
            title: '💳 Paiement SumUp',
            description: 'Une fenêtre sécurisée s\'est ouverte pour votre paiement',
            status: 'info',
            duration: 5000,
          });
          
          onPaymentClose();
          
          const checkInterval = setInterval(() => {
            if (sumupWindow.closed) {
              clearInterval(checkInterval);
              
              setTimeout(() => {
                fetchContributions();
                toast({
                  title: '🔄 Actualisation',
                  description: 'Vérification de votre paiement...',
                  status: 'info',
                  duration: 3000,
                });
              }, 2000);
            }
          }, 500);
          
          setTimeout(() => {
            if (!sumupWindow.closed) {
              clearInterval(checkInterval);
            }
          }, 600000);
          
        } else {
          toast({
            title: '⚠️ Popup bloquée',
            description: (
              <div>
                <p>Votre navigateur bloque les popups.</p>
                <p style={{marginTop: '8px'}}>
                  Veuillez autoriser les popups pour ce site ou 
                  <a href={sumupUrl} target="_blank" rel="noopener noreferrer" 
                     style={{color: '#3182ce', textDecoration: 'underline', marginLeft: '4px'}}>
                    cliquez ici pour payer
                  </a>
                </p>
              </div>
            ),
            status: 'warning',
            duration: 10000,
            isClosable: true,
          });
        }
      } else if (paymentMethod === 'stripe') {
        // Créer une popup centrée avec dimensions personnalisées
        const width = 800;
        const height = 900;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        const popupFeatures = `
          width=${width},
          height=${height},
          left=${left},
          top=${top},
          toolbar=no,
          location=no,
          directories=no,
          status=no,
          menubar=no,
          scrollbars=yes,
          resizable=yes`.replace(/\s+/g, '');
        
        const stripeWindow = ouvrirFenetre(response.url, 'StripePayment', popupFeatures);
        
        if (stripeWindow) {
          // Mettre le focus sur la popup
          stripeWindow.focus();
          
          toast({
            title: '💳 Paiement Stripe',
            description: 'Une fenêtre sécurisée s\'est ouverte pour votre paiement',
            status: 'info',
            duration: 5000,
          });
          
          // Fermer le modal de sélection
          onPaymentClose();
          
          // Surveiller la fermeture de la popup
          const checkInterval = setInterval(() => {
            if (stripeWindow.closed) {
              clearInterval(checkInterval);
              
              // Attendre un peu que le webhook traite le paiement
              setTimeout(() => {
                fetchContributions();
                toast({
                  title: '🔄 Actualisation',
                  description: 'Vérification de votre paiement...',
                  status: 'info',
                  duration: 3000,
                });
              }, 2000);
            }
          }, 500);
          
          // Timeout de sécurité (10 minutes)
          setTimeout(() => {
            if (!stripeWindow.closed) {
              clearInterval(checkInterval);
            }
          }, 600000);
          
        } else {
          // Si la popup est bloquée
          toast({
            title: '⚠️ Popup bloquée',
            description: (
              <div>
                <p>Votre navigateur bloque les popups.</p>
                <p style={{marginTop: '8px'}}>
                  Veuillez autoriser les popups pour ce site ou 
                  <a href={response.url} target="_blank" rel="noopener noreferrer" 
                     style={{color: '#3182ce', textDecoration: 'underline', marginLeft: '4px'}}>
                    cliquez ici pour payer
                  </a>
                </p>
              </div>
            ),
            status: 'warning',
            duration: 10000,
            isClosable: true,
          });
        }
        
      } else if (paymentMethod === 'paypal') {
        // Même approche pour PayPal
        const width = 800;
        const height = 900;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        const popupFeatures = `
          width=${width},
          height=${height},
          left=${left},
          top=${top},
          toolbar=no,
          location=no,
          directories=no,
          status=no,
          menubar=no,
          scrollbars=yes,
          resizable=yes`.replace(/\s+/g, '');
        
        const paypalWindow = ouvrirFenetre(response.approvalUrl, 'PayPalPayment', popupFeatures);
        
        if (paypalWindow) {
          paypalWindow.focus();
          
          toast({
            title: '💰 Paiement PayPal',
            description: 'Une fenêtre sécurisée s\'est ouverte pour votre paiement',
            status: 'info',
            duration: 5000,
          });
          
          onPaymentClose();
          
          // Surveiller la fermeture de la popup PayPal
          const checkInterval = setInterval(() => {
            if (paypalWindow.closed) {
              clearInterval(checkInterval);
              
              setTimeout(() => {
                fetchContributions();
                toast({
                  title: '🔄 Actualisation',
                  description: 'Vérification de votre paiement...',
                  status: 'info',
                  duration: 3000,
                });
              }, 2000);
            }
          }, 500);
          
          setTimeout(() => {
            if (!paypalWindow.closed) {
              clearInterval(checkInterval);
            }
          }, 600000);
          
        } else {
          toast({
            title: '⚠️ Popup bloquée',
            description: (
              <div>
                <p>Votre navigateur bloque les popups.</p>
                <p style={{marginTop: '8px'}}>
                  Veuillez autoriser les popups pour ce site ou 
                  <a href={response.approvalUrl} target="_blank" rel="noopener noreferrer" 
                     style={{color: '#3182ce', textDecoration: 'underline', marginLeft: '4px'}}>
                    cliquez ici pour payer
                  </a>
                </p>
              </div>
            ),
            status: 'warning',
            duration: 10000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  const downloadReceipt = async (contributionId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/contributions/${contributionId}/receipt`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement du reçu');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recu_contribution_${contributionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: 'Succès',
        description: 'Reçu téléchargé avec succès',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de télécharger le reçu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const downloadAnnualReceipt = async () => {
    try {
      console.log('📥 Downloading receipt for year:', receiptYear);
      console.log('📋 User matricule:', user.matricule);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/contributions/fiscal-receipt/${receiptYear}/download`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        }
      );
      
      console.log('📡 Response status:', response.status);
      console.log('📄 Content-Type:', response.headers.get('content-type'));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        let serverMessage = '';
        try { serverMessage = JSON.parse(errorText).error || ''; } catch (e) { /* réponse non JSON */ }
        throw new Error(serverMessage || `Erreur lors du téléchargement du reçu (${response.status})`);
      }
      
      const blob = await response.blob();
      console.log('📦 Blob received:', blob.size, 'bytes, type:', blob.type);
      
      if (blob.size === 0) {
        throw new Error('Le fichier PDF est vide');
      }
      
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `recu_fiscal_${receiptYear}_${user.matricule}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
        document.body.removeChild(a);
      }, 100);
      
      toast({
        title: 'Succès',
        description: 'Reçu fiscal téléchargé',
        status: 'success',
        duration: 3000,
      });
      
      onAnnualReceiptClose();
    } catch (error) {
      console.error('❌ Error downloading receipt:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de télécharger le reçu fiscal',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'paye': return 'green';
      case 'en_attente': return 'orange';
      case 'refuse': return 'red';
      case 'rembourse': return 'purple';
      case 'echoue': return 'gray';
      default: return 'gray';
    }
  };

  const getStatusLabel = (statut) => {
    switch (statut) {
      case 'paye': return 'Payé';
      case 'en_attente': return 'En attente';
      case 'refuse': return 'Refusé';
      case 'rembourse': return 'Remboursé';
      case 'echoue': return 'Échoué';
      default: return statut;
    }
  };

  const formatAmount = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const years = [...new Set(contributions.map(c => new Date(c.date_contribution).getFullYear()))];
  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  
  // Années disponibles pour le reçu fiscal (exclut l'année en cours)
  const availableYears = Array.from(
    { length: 5 }, 
    (_, i) => new Date().getFullYear() - 1 - i
  );

  // Trouver le type de cotisation sélectionné
  const selectedType = contributionTypes.find(t => t.id === parseInt(typeId));

  // Paiement rapide (Stripe ou PayPal) depuis une carte de la section "Paiement en ligne rapide"
  const handleQuickPay = async (type, method) => {
    try {
      const response = await apiService.makeRequest('/contributions/payment/create', {
        method: 'POST',
        body: JSON.stringify({
          amount: parseFloat(type.montant_suggere),
          type_id: type.id,
          type_name: type.nom,
          paymentMethod: method,
        }),
      });

      const paymentUrl = method === 'paypal' ? response.approvalUrl : response.url;

      const width = 800;
      const height = 900;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      const popupFeatures = `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes`.replace(/\s+/g, '');

      const popup = ouvrirFenetre(paymentUrl, method === 'paypal' ? 'PayPalPayment' : 'StripePayment', popupFeatures);

      if (popup) {
        popup.focus();
        toast({
          title: method === 'paypal' ? '💰 Paiement PayPal' : '💳 Paiement Stripe',
          description: 'Une fenêtre sécurisée s\'est ouverte pour votre paiement',
          status: 'info',
          duration: 5000,
        });

        const checkInterval = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkInterval);
            setTimeout(() => {
              fetchContributions();
              toast({
                title: '🔄 Actualisation',
                description: 'Vérification de votre paiement...',
                status: 'info',
                duration: 3000,
              });
            }, 2000);
          }
        }, 500);

        setTimeout(() => {
          if (!popup.closed) clearInterval(checkInterval);
        }, 600000);
      } else {
        toast({
          title: '⚠️ Popup bloquée',
          description: (
            <div>
              <p>Votre navigateur bloque les popups.</p>
              <p style={{ marginTop: '8px' }}>
                Veuillez autoriser les popups ou
                <a href={paymentUrl} target="_blank" rel="noopener noreferrer"
                   style={{ color: '#3182ce', textDecoration: 'underline', marginLeft: '4px' }}>
                  cliquez ici pour payer
                </a>
              </p>
            </div>
          ),
          status: 'warning',
          duration: 10000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur de paiement',
        description: error.message || 'Une erreur est survenue',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Cartes de paiement rapide : une par type suggéré (CB), + une carte PayPal
  // dédiée pour la cotisation mensuelle (équilibre aussi la grille à 3 colonnes)
  const quickPayCards = contributionTypes
    .filter(type => type.montant_suggere)
    .flatMap(type => {
      const cards = [{ type, method: 'stripe' }];
      if (type.nom === 'Cotisation mensuelle') {
        cards.push({ type, method: 'paypal' });
      }
      return cards;
    });

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={3}>
        <Heading size="lg">Mes Cotisations</Heading>
        <HStack flexWrap="wrap">
          <Button colorScheme="blue" onClick={onAnnualReceiptOpen}>
            Reçu fiscal annuel
          </Button>
          <Button colorScheme="green" onClick={onPaymentOpen}>
            Effectuer une cotisation
          </Button>
        </HStack>
      </Flex>

      {/* Section Paiement Rapide avec Stripe */}
      <Card mb={6} bg="blue.50" borderColor="blue.200" borderWidth="1px">
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack spacing={2}>
              <Icon as={FaCreditCard} color="blue.600" boxSize={5} />
              <Heading size="md" color="blue.800">Paiement en ligne rapide</Heading>
            </HStack>
            
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">Paiement sécurisé avec CB</AlertTitle>
                <AlertDescription fontSize="xs">
                  Payez vos cotisations directement en ligne avec votre carte bancaire de manière sécurisée
                </AlertDescription>
              </Box>
            </Alert>

            {typesLoading ? (
              <Flex justify="center" py={4}>
                <Spinner color="blue.500" />
              </Flex>
            ) : quickPayCards.length > 0 ? (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {quickPayCards.map(({ type, method }) => (
                  <Box
                    key={`${type.id}-${method}`}
                    p={4}
                    bg="white"
                    borderRadius="md"
                    border="1px"
                    borderColor="gray.200"
                    _hover={{ shadow: 'md', borderColor: 'blue.300' }}
                    transition="all 0.2s"
                  >
                    <VStack spacing={3} align="stretch">
                      <Text fontWeight="bold" fontSize="lg" color="gray.800">
                        {type.nom}
                      </Text>
                      {type.description && (
                        <Text fontSize="sm" color="gray.600" noOfLines={2}>
                          {type.description}
                        </Text>
                      )}
                      <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                        {formatAmount(type.montant_suggere)}
                      </Text>
                      <Button
                        colorScheme={method === 'paypal' ? 'yellow' : 'blue'}
                        leftIcon={method === 'paypal' ? <FaPaypal /> : <FaCreditCard />}
                        size="md"
                        width="full"
                        onClick={() => handleQuickPay(type, method)}
                      >
                        {method === 'paypal' ? 'Payer avec PayPal' : 'Payer avec Carte Bancaire'}
                      </Button>
                    </VStack>
                  </Box>
                ))}
              </SimpleGrid>
            ) : (
              <Alert status="warning">
                <AlertIcon />
                <Text fontSize="sm">Aucun type de cotisation disponible pour le moment</Text>
              </Alert>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Filtres */}
      <Card mb={6}>
        <CardBody>
          <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
            <FormControl>
              <FormLabel>Type</FormLabel>
              <Select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                <option value="all">Tous les types</option>
                {contributionTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.nom}</option>
                ))}
              </Select>
            </FormControl>
  
            <FormControl>
              <FormLabel>Année</FormLabel>
              <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                <option value="all">Toutes les années</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Mois</FormLabel>
              <Select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}>
                <option value="all">Tous les mois</option>
                {months.map((month, idx) => (
                  <option key={idx} value={idx}>{month}</option>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </CardBody>
      </Card>

      {/* Tableau des cotisations */}
      <Card>
        <CardBody>
          {loading ? (
            <Flex justify="center" align="center" py={12}>
              <VStack spacing={4}>
                <Spinner size="xl" color="blue.500" />
                <Text color="gray.600">Chargement des contributions...</Text>
              </VStack>
            </Flex>
          ) : isMobile ? (
            <Stack spacing={3} divider={<Divider />}>
              {filteredContributions.length === 0 ? (
                <Text color="gray.500" textAlign="center" py={6}>Aucune contribution trouvée</Text>
              ) : (
                filteredContributions.map(contrib => (
                  <Box key={contrib.id}>
                    <HStack justify="space-between" mb={2}>
                      <Badge colorScheme="blue">{contrib.type_nom || 'Non spécifié'}</Badge>
                      <Badge colorScheme={getStatusColor(contrib.statut)}>
                        {getStatusLabel(contrib.statut)}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="sm" color="gray.500">
                        {new Date(contrib.date_contribution).toLocaleDateString('fr-FR')}
                      </Text>
                      <Text fontWeight="bold">{contrib.montant} €</Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.600" mb={2}>
                      {MODE_LABELS[contrib.mode_paiement] || '-'}
                    </Text>
                    {contrib.statut === 'paye' && (
                      <Button
                        size="sm"
                        colorScheme="blue"
                        w="full"
                        onClick={() => downloadReceipt(contrib.id)}
                      >
                        Télécharger le reçu
                      </Button>
                    )}
                  </Box>
                ))
              )}
            </Stack>
          ) : (
            <>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>Type</Th>
                    <Th>Montant</Th>
                    <Th>Statut</Th>
                    <Th>Mode</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredContributions.map(contrib => (
                    <Tr key={contrib.id}>
                      <Td>{new Date(contrib.date_contribution).toLocaleDateString('fr-FR')}</Td>
                      <Td>
                        <Badge colorScheme="blue">
                          {contrib.type_nom || 'Non spécifié'}
                        </Badge>
                      </Td>
                      <Td fontWeight="bold">{contrib.montant} €</Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(contrib.statut)}>
                          {getStatusLabel(contrib.statut)}
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                          {MODE_LABELS[contrib.mode_paiement] || '-'}
                        </Text>
                      </Td>
                      <Td>
                        {contrib.statut === 'paye' && (
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={() => downloadReceipt(contrib.id)}
                          >
                            Télécharger le reçu
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>

              {filteredContributions.length === 0 && !loading && (
                <Box textAlign="center" py={8}>
                  <Text color="gray.500" fontSize="lg">
                    {contributions.length === 0 
                      ? "Vous n'avez pas encore effectué de cotisation" 
                      : "Aucune cotisation ne correspond aux filtres sélectionnés"}
                  </Text>
                </Box>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Modal de paiement (autres méthodes) */}
      <Modal isOpen={isPaymentOpen} onClose={onPaymentClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Effectuer une cotisation</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle fontSize="sm">Paiement rapide</AlertTitle>
                  <AlertDescription fontSize="xs">
                    Pour un paiement rapide et sécurisé, utilisez les boutons "Payer avec Carte Bancaire" ci-dessus
                  </AlertDescription>
                </Box>
              </Alert>

              <FormControl isRequired>
                <FormLabel>Type de cotisation</FormLabel>
                <Select 
                  value={typeId} 
                  onChange={(e) => setTypeId(e.target.value)}
                  placeholder="Sélectionner..."
                  isDisabled={typesLoading}
                >
                  {contributionTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.nom}
                      {type.montant_suggere && ` (Suggéré: ${type.montant_suggere}€)`}
                    </option>
                  ))}
                </Select>
                {typesLoading && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Chargement des types...
                  </Text>
                )}
                {!typesLoading && contributionTypes.length === 0 && (
                  <Text fontSize="xs" color="red.500" mt={1}>
                    Aucun type de cotisation disponible
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Montant (€)</FormLabel>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="50"
                  min="1"
                  step="0.01"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Méthode de paiement</FormLabel>
                <Select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                  <option value="stripe">💳 Carte bancaire (Stripe)</option>
                  <option value="paypal">💰 PayPal</option>
                  <option value="sumup">💳 SumUp</option>
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPaymentClose}>
              Annuler
            </Button>
            <Button 
              colorScheme="green" 
              onClick={handlePayment}
              isDisabled={!typeId || !amount || typesLoading}
              leftIcon={
                paymentMethod === 'sumup' ? <FaCreditCard /> :
                paymentMethod === 'paypal' ? <FaPaypal /> : <FaCreditCard />
              }
            >
              {paymentMethod === 'sumup' ? 'Utiliser SumUp' : 'Payer'}
              {amount && selectedType ? ` ${amount} € - ${selectedType.nom}` : ''}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal téléchargement reçu fiscal annuel */}
      <Modal isOpen={isAnnualReceiptOpen} onClose={onAnnualReceiptClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Télécharger mon reçu fiscal annuel</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Téléchargez le reçu fiscal récapitulatif de toutes vos contributions pour une année donnée.
                Le reçu de l'année en cours ne sera disponible qu'à partir de janvier de l'année suivante.
              </Text>
              <FormControl isRequired>
                <FormLabel>Année fiscale</FormLabel>
                <Select 
                  value={receiptYear} 
                  onChange={(e) => setReceiptYear(parseInt(e.target.value))}
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </Select>
              </FormControl>
              <Box bg="blue.50" p={3} borderRadius="md">
                <Text fontSize="sm" fontWeight="medium" color="blue.800">
                  Ce reçu fiscal peut être utilisé pour votre déclaration d'impôts.
                </Text>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAnnualReceiptClose}>
              Annuler
            </Button>
            <Button colorScheme="blue" onClick={downloadAnnualReceipt}>
              Télécharger le reçu {receiptYear}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}